import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;

    return {
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        productsDataView: getProductListDataView(state), // VERNDALE CUSTOMIZATION: Added productsDataView
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    resultsMatchMessageWrapper?: InjectableCss; // VERNDALE CUSTOMIZATION
    resultsMatchMessage?: TypographyPresentationProps; // VERNDALE CUSTOMIZATION
}

export const productCountStyles: ProductListProductCountStyles = {
    wrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
    // VERNDALE CUSTOMIZATION
    resultsMatchMessageWrapper: {
        css: css`
            display: flex;
            justify-content: left;
            padding: 0;
        `,
    },
    resultsMatchMessage: {
        size: 12,
        css: css`
            font-style: italic;
            font-weight: 400;
            color: ${getColor("danger.main")};
        `,
    },
};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({ totalItemCount, query, productsDataView }) => {
    if (!totalItemCount) {
        return null;
    }

    const itemsText = (totalItemCount !== 1 ? " items" : " item") + (query ? " for " : "");

    // VERNDALE CUSTOMIZATION START - Added productsDataView
    const products = productsDataView?.value;

    const hasExactMatch = products?.some(
        product =>
            product.manufacturerItem?.toLowerCase() === query?.toLowerCase() ||
            product.productNumber?.toLowerCase() === query?.toLowerCase() ||
            product.productTitle?.toLowerCase().includes(query?.toLowerCase() || ""),
    );
    // VERNDALE CUSTOMIZATION

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.countText} data-test-selector="productListCountText">
                {totalItemCount}
            </Typography>
            <Typography {...styles.itemsText}>{translate(itemsText)}</Typography>
            {query && (
                <Typography {...styles.queryText} data-test-selector="productListQueryText">
                    {query}
                </Typography>
            )}
            {/* VERNDALE CUSTOMIZATION START */}
            {!hasExactMatch && products?.length === 1 && (
                <StyledWrapper {...styles.resultsMatchMessageWrapper}>
                    <Typography {...styles.resultsMatchMessage}>
                        {siteMessage("SearchInput_NotExactMatchMessage")}
                    </Typography>
                </StyledWrapper>
            )}
            {/* VERNDALE CUSTOMIZATION END */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListProductCount),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
